import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Coins, Cpu, Activity, Gauge, Download, RefreshCw, WifiOff, Loader2,
  Building2, Bug, CheckCircle2, Hash,
} from "lucide-react";
import PageHeader from "@/components/dashboard/PageHeader";
import StatCard from "@/components/dashboard/StatCard";
import DataTable from "@/components/dashboard/DataTable";
import LoadError from "@/components/dashboard/LoadError";
import { BarRow, GroupedBars } from "@/components/dashboard/Charts";
import Button from "@/components/ui/Button";
import { cn } from "@/lib/cn";
import { getAnalytics } from "@/api/admin";
import { dateAxis } from "@/lib/flows";
import { AGENT_COST } from "@/data/mock";

const RANGES = [
  { days: 7, label: "7d" },
  { days: 30, label: "30d" },
  { days: 90, label: "90d" },
];

const usd = (n) => `$${Number(n || 0).toFixed(2)}`;
// Spend is charted in dollars, but a day's platform spend is often cents, and
// `$0.00` on every gridline makes a chart that cannot be read. Show enough
// precision to tell one bar from another, and drop it again once the numbers are
// large enough not to need it.
function usdAxis(n) {
  const v = Number(n || 0);
  if (v === 0) return "$0";
  if (v >= 1000) return `$${Math.round(v).toLocaleString()}`;
  if (v >= 1) return `$${v.toFixed(2)}`;
  // Sub-dollar: keep enough decimals for two gridlines to differ, then trim the
  // trailing zeros that made the half-way tick read "$0.500".
  return `$${v.toFixed(4).replace(/0+$/, "").replace(/\.$/, "")}`;
}

// CSV cells are free text (a tenant name may contain a comma or a quote), which
// used to shift every column after it in the exported file.
function cell(v) {
  const s = String(v ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

// Deterministic demo payload for offline mode.
function demoAnalytics(days) {
  const today = new Date();
  const series = Array.from({ length: days }, (_, i) => {
    const d = new Date(today); d.setDate(today.getDate() - (days - 1 - i));
    return { date: d.toISOString().slice(0, 10), cost: Number((0.4 + Math.abs(Math.sin(i * 1.3)) * 1.8).toFixed(3)), runs: Math.round(2 + Math.abs(Math.sin(i * 0.9)) * 9) };
  });
  const by_agent = AGENT_COST.map((a) => ({ agent: a.agent, calls: a.calls, cost: a.cost, tokens: a.calls * 1500 }));
  const cost = by_agent.reduce((s, a) => s + a.cost, 0);
  const calls = by_agent.reduce((s, a) => s + a.calls, 0);
  const runs = series.reduce((s, x) => s + x.runs, 0);
  return {
    days,
    totals: { cost, runs, calls, tokens: by_agent.reduce((s, a) => s + a.tokens, 0), avg_per_run: runs ? cost / runs : 0, bugs: 14, pass_rate: 72 },
    by_agent,
    series,
    by_company: [
      { company_id: "c1", name: "Bizzfly", code: "C1DEMO", cost: cost * 0.42 },
      { company_id: "c2", name: "Northwind", code: "C2DEMO", cost: cost * 0.28 },
      { company_id: "c3", name: "Acme QA", code: "C3DEMO", cost: cost * 0.18 },
      { company_id: "c4", name: "Pixel & Co", code: "C4DEMO", cost: cost * 0.12 },
    ],
  };
}

function toCsv(d) {
  const lines = ["Metric,Value"];
  lines.push(`Window (days),${d.days}`);
  lines.push(`Total spend (USD),${d.totals.cost}`);
  lines.push(`Model calls,${d.totals.calls}`);
  lines.push(`Total runs,${d.totals.runs}`);
  lines.push(`Tokens,${d.totals.tokens}`);
  lines.push(`Avg per run (USD),${d.totals.avg_per_run}`);
  lines.push(`Bugs,${d.totals.bugs}`);
  lines.push(`Pass rate (%),${d.totals.pass_rate}`);
  lines.push("");
  lines.push("Agent,Model calls,Cost (USD),Tokens");
  d.by_agent.forEach((a) => lines.push(`${cell(a.agent)},${a.calls},${a.cost},${a.tokens}`));
  lines.push("");
  lines.push("Date,Cost (USD),Runs");
  d.series.forEach((s) => lines.push(`${cell(s.date)},${s.cost},${s.runs}`));
  lines.push("");
  lines.push("Company,Code,Cost (USD)");
  d.by_company.forEach((c) => lines.push(`${cell(c.name)},${cell(c.code || "")},${c.cost}`));
  return lines.join("\n");
}

export default function Analytics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [demo, setDemo] = useState(false);
  const [error, setError] = useState(null);
  // Every heading, label and export filename is derived from the `days` control,
  // so a slow earlier response landing after a newer one left the page showing
  // one window's data under another window's title. Only the newest request wins.
  const reqRef = useRef(0);
  // A refresh already in flight. `reqRef` only decides which RESPONSE wins; it
  // does nothing about a second request being sent, so a double-click fired two
  // identical queries and threw one away.
  const inFlight = useRef(false);

  const load = useCallback(async () => {
    if (inFlight.current) return;          // ignore a second click, don't queue it
    inFlight.current = true;
    const reqId = ++reqRef.current;
    setLoading(true);
    setError(null);
    try {
      const d = await getAnalytics(days);
      if (reqId !== reqRef.current) return;
      setData(d);
      setDemo(false);
    } catch (e) {
      if (reqId !== reqRef.current) return;
      // Only an unreachable backend (ApiError status 0) justifies the fixture.
      // A 500/403 used to land here too, so a server fault was reported as
      // "backend not reachable" alongside invented platform financials.
      if (e && e.status === 0) {
        setData(demoAnalytics(days));
        setDemo(true);
      } else {
        setData(null);
        setDemo(false);
        setError((e && e.message) || "Failed to load analytics.");
      }
    } finally {
      inFlight.current = false;
      if (reqId === reqRef.current) setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const d = data || demoAnalytics(days);
  const totals = d.totals;
  // A live backend returning no per-agent usage means exactly that; only the
  // demo path may substitute the fixture.
  const agents = d.by_agent.length ? d.by_agent : demo ? demoAnalytics(days).by_agent : [];
  const maxCost = Math.max(...agents.map((a) => a.cost), 0.0001);
  const totalCost = agents.reduce((s, a) => s + a.cost, 0);

  const spendData = d.series.map((s) => s.cost);
  const runsData = d.series.map((s) => s.runs);
  // One dated axis for both charts, from the shared builder that also labels the
  // company Analytics page — so a bar means the same thing on either.
  //
  // `labels` thins itself with the window (a weekday per bar up to 7 days, then
  // every ceil(days/6)-th date), which is what keeps a 90-day axis readable
  // instead of 90 overlapping ticks. `titles` stays a full date on EVERY bar, so
  // a hover always names its own day even where the visible tick is blank.
  const axis = useMemo(() => dateAxis(d.series.map((s) => s.date)), [d.series]);
  const seriesTitles = axis.titles;

  const maxCompany = Math.max(...d.by_company.map((c) => c.cost), 0.0001);

  // The three figures this card adds to the KPI row above it.
  //
  // "Total spend" and "Avg / run" used to appear here as well as in the header,
  // so the same two numbers were on screen twice within one viewport. The header
  // is where a page-level total belongs, so they were dropped from here — the
  // underlying `totals.cost` / `totals.avg_per_run` are untouched and still feed
  // the KPI row, the CSV export and the chart.
  const quick = useMemo(() => ([
    { icon: Hash, label: "Tokens", value: Number(totals.tokens || 0).toLocaleString() },
    { icon: Bug, label: "Bugs found", value: totals.bugs ?? 0 },
    { icon: CheckCircle2, label: "Pass rate", value: `${totals.pass_rate ?? 0}%` },
  ]), [totals]);

  const columns = [
    { key: "agent", header: "Agent", render: (a) => <span className="font-medium text-slate-800 dark:text-white">{a.agent}</span> },
    // "Model Calls", not "Calls": the value is the number of requests issued to
    // an LLM provider on that agent's runs (see admin._model_calls), which is
    // not the number of runs and not the number of steps.
    { key: "calls", header: "Model Calls", sortable: true, align: "right", render: (a) => <span className="tabular-nums">{Number(a.calls || 0).toLocaleString()}</span> },
    { key: "tokens", header: "Tokens", sortable: true, align: "right", render: (a) => <span className="tabular-nums text-slate-500 dark:text-slate-400">{Number(a.tokens || 0).toLocaleString()}</span> },
    { key: "cost", header: "Cost", sortable: true, align: "right", render: (a) => <span className="tabular-nums font-semibold text-slate-800 dark:text-white">{usd(a.cost)}</span> },
    { key: "share", header: "Share", align: "right", render: (a) => <span className="text-xs text-slate-400">{totalCost ? Math.round((a.cost / totalCost) * 100) : 0}%</span> },
  ];

  // Exporting while the fixture is on screen wrote invented figures to a file
  // that carries no hint they aren't real, so the demo/error states can't export.
  function exportCsv() {
    if (demo || error || !data) return;
    const blob = new Blob([toCsv(d)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `platform-analytics-${days}d.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <>
      <PageHeader
        title="Cost & Token Analytics"
        subtitle="Platform-wide AI spend — aggregated across every tenant's runs."
        actions={
          <>
            <div className="flex rounded-xl border border-slate-200 bg-slate-50 p-0.5 dark:border-white/10 dark:bg-white/5">
              {RANGES.map((r) => (
                <button
                  key={r.days}
                  type="button"
                  onClick={() => setDays(r.days)}
                  className={cn(
                    "rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors",
                    days === r.days ? "bg-brand-600 text-white shadow-soft" : "text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white"
                  )}
                >
                  {r.label}
                </button>
              ))}
            </div>
            {/* Refresh re-runs the SAME query — the selected window, and every
                metric, chart and table on the page, all of which come from this
                one response. It used to call `load` with no visible effect of
                any kind: the spinner below is gated on `!data`, so with data on
                screen a refresh changed nothing a user could see and read as a
                dead button. */}
            <Button variant="outline" size="sm" onClick={load} disabled={loading}>
              <RefreshCw size={16} className={cn(loading && "animate-spin")} />
              {loading ? "Refreshing…" : "Refresh"}
            </Button>
            <Button variant="outline" size="sm" onClick={exportCsv} disabled={demo || !!error || !data}><Download size={16} /> Export CSV</Button>
          </>
        }
      />

      {demo && (
        <div className="mb-5 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-2.5 text-sm text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
          <WifiOff size={15} /> Showing demo data — backend not reachable.
        </div>
      )}

      <LoadError message={error} onRetry={load} />

      {loading && !data ? (
        <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 size={26} className="animate-spin" /></div>
      ) : error ? null : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard icon={Coins} label="Total spend" value={usd(totals.cost)} accent="amber" sub={`last ${days} days`} spark={spendData} sparkTitles={seriesTitles} sparkFormat={usd} />
            <StatCard icon={Activity} label="Model calls" value={Number(totals.calls).toLocaleString()} accent="sky" sub="across all agents" delay={0.05} />
            <StatCard icon={Cpu} label="Total runs" value={Number(totals.runs).toLocaleString()} sub="platform-wide" delay={0.1} />
            <StatCard icon={Gauge} label="Avg / run" value={`$${Number(totals.avg_per_run || 0).toFixed(4)}`} accent="emerald" sub="cost per run" delay={0.15} />
          </div>

          {/* spend over time + top companies */}
          <div className="mt-6 grid gap-4 lg:grid-cols-3">
            <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="card p-5 lg:col-span-2">
              {/* No total repeated here — "Total spend" is the first KPI directly
                  above this card, and the same figure twice in one viewport is
                  what the duplicate was reported for. */}
              <div>
                <h3 className="text-sm font-semibold text-slate-800 dark:text-white">Spend over time</h3>
                <p className="text-xs text-slate-400">
                  Daily AI spend in USD · last {days} days
                </p>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                {quick.map((q) => (
                  <div key={q.label} className="rounded-xl bg-slate-50 p-3 dark:bg-white/5">
                    <span className="grid h-7 w-7 place-items-center rounded-lg bg-brand-500/10 text-brand-600 dark:text-brand-300"><q.icon size={14} /></span>
                    <p className="mt-1.5 font-display text-base font-bold text-slate-900 dark:text-white">{q.value}</p>
                    <p className="text-[0.6875rem] text-slate-400">{q.label}</p>
                  </div>
                ))}
              </div>

              {/* GroupedBars, not the Sparkline this replaces. A sparkline is a
                  decorative shape: no value axis, no dated ticks and no tooltip,
                  so a reader could see that spend moved but never what it was, on
                  which day, or against what scale. This is the chart the rest of
                  the product already uses for a dated series, and it brings the
                  value ticks, gridlines, per-bar tooltips and a real empty state
                  with it. */}
              <div className="mt-5">
                <GroupedBars
                  labels={axis.labels}
                  titles={seriesTitles}
                  series={[{ key: "spend", label: "Spend", color: "bg-amber-500", data: spendData }]}
                  height={150}
                  format={usdAxis}
                  empty={`No spend recorded in the last ${days} days.`}
                />
              </div>
              <div className="mt-4 border-t border-slate-100 pt-4 dark:border-white/5">
                <p className="mb-2 text-xs font-medium text-slate-500 dark:text-slate-400">Runs per day</p>
                {/* The day labels used to be dropped entirely past 10 days
                    (`labels = days <= 10 ? … : undefined`), so on the default
                    30-day window every bar was anonymous. Same thinned axis as
                    the chart above, so the two read as one timeline. */}
                <GroupedBars
                  labels={axis.labels}
                  titles={seriesTitles}
                  series={[{ key: "runs", label: "Runs", color: "bg-brand-500", data: runsData }]}
                  height={110}
                  empty={`No runs in the last ${days} days.`}
                />
              </div>
            </motion.div>

            {/* self-start: a grid item stretches to the tallest row-mate by
                default, so this card was stretched to the full height of the
                two-column chart beside it and padded the difference with empty
                space — a short tenant list left most of the card blank. Sizing to
                its own content is the fix; no fixed or minimum height is
                involved, so it stays correct for one company or for twenty. */}
            <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.08 }} className="card self-start p-5">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-white"><Building2 size={15} /> Top companies by spend</h3>
              <div className="mt-4 space-y-3">
                {d.by_company.length === 0 ? (
                  <p className="py-6 text-center text-sm text-slate-400">No tenant spend yet.</p>
                ) : d.by_company.map((c) => (
                  <div key={c.company_id} className="flex items-center gap-3">
                    <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-brand-400/20 to-brand-600/20 text-[0.625rem] font-bold text-brand-700 dark:text-brand-300">{(c.name || "?").slice(0, 2).toUpperCase()}</span>
                    <div className="min-w-0 flex-1">
                      {/* Name AND code. `companies.name` has no uniqueness
                          constraint, so two real tenants can share one and the
                          name alone cannot tell them apart. The code is served by
                          the API from stored identity (company_ref, else the
                          company's own id) — never derived from the name, which
                          would be identical for both. */}
                      <p className="truncate text-sm font-medium text-slate-700 dark:text-slate-200"
                         title={c.code ? `${c.name} (${c.code})` : c.name}>
                        {c.name}
                        {c.code && (
                          <span className="ml-1.5 font-mono text-xs font-normal text-slate-400">({c.code})</span>
                        )}
                      </p>
                      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-100 dark:bg-white/10">
                        <div className="h-full rounded-full bg-gradient-to-r from-brand-500 to-brand-400" style={{ width: `${Math.round((c.cost / maxCompany) * 100)}%` }} />
                      </div>
                    </div>
                    <span className="shrink-0 text-sm font-semibold text-slate-800 dark:text-white">{usd(c.cost)}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* cost by agent + agent table */}
          <div className="mt-4 grid gap-4 lg:grid-cols-5">
            <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="card p-5 lg:col-span-2">
              <h3 className="text-sm font-semibold text-slate-800 dark:text-white">Cost by agent</h3>
              <div className="mt-4 space-y-3">
                {agents.length === 0 ? (
                  <p className="py-6 text-center text-sm text-slate-400">No agent cost in this window.</p>
                ) : (
                  agents.map((a) => (
                    <BarRow key={a.agent} label={a.agent} value={a.cost} max={maxCost} display={usd(a.cost)} />
                  ))
                )}
              </div>
            </motion.div>

            <div className="lg:col-span-3">
              <DataTable
                columns={columns}
                rows={agents}
                getRowId={(a) => a.agent}
                initialSort={{ key: "cost", dir: "desc" }}
                maxHeight="22rem"
                pageSize={8}
                emptyLabel="No agent usage in this window."
              />
            </div>
          </div>
        </>
      )}
    </>
  );
}
